/**
 * Utility to proxy external images through our backend for compression and caching.
 * Only proxies hero/large images. Product grid images use direct URLs with lazy loading.
 */
export const proxyImage = (url, width = 400, quality = 75) => {
  if (!url) return '';
  // Proxy customer-assets images for size reduction
  if (url.startsWith('https://customer-assets.emergentagent.com/') || url.startsWith('https://customer-assets')) {
    return `/api/image-proxy?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
  }
  return url;
};

/**
 * Force-proxy any image regardless of size (for specific cases like hero backgrounds).
 */
export const proxyImageForce = (url, width = 400, quality = 75) => {
  if (!url) return '';
  if (url.startsWith('https://customer-assets.emergentagent.com/') || url.startsWith('https://')) {
    return `/api/image-proxy?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
  }
  return url;
};

/**
 * Process an entire IMAGES object — proxies all customer-assets images at specified width.
 */
export const proxyImages = (imagesObj, width = 400, quality = 65) => {
  const proxied = {};
  for (const [key, value] of Object.entries(imagesObj)) {
    if (value && value.startsWith('https://customer-assets')) {
      proxied[key] = `/api/image-proxy?url=${encodeURIComponent(value)}&w=${width}&q=${quality}`;
    } else {
      proxied[key] = value;
    }
  }
  return proxied;
};

const FALLBACK_PHOTOS = {
  Wine: 'https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/5u8z7fb5_IMG_8856.jpeg',
  Spirits: 'https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/9v5y4ryl_6D4ACE4B-0107-4C36-B439-21529139190F-scaled.jpeg.webp',
  Beer: '/images/blog/brunchi-shop-craft-beer-fridge-marina-rubicon-playa-blanca.jpg',
  default: 'https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg',
};

// Real shop photo for a category, used when a product has no (working) photo
export const fallbackPhoto = (category, width = 400) => proxyImage(FALLBACK_PHOTOS[category] || FALLBACK_PHOTOS.default, width);

// onError: retry once (covers brief proxy timeouts), then swap to a real category photo
export const imgFallback = (category, width = 400) => (e) => {
  const img = e.currentTarget;
  if (!img.dataset.retried) {
    img.dataset.retried = '1';
    const src = img.src;
    setTimeout(() => { img.src = src + (src.includes('?') ? '&' : '?') + 'r=1'; }, 1500);
    return;
  }
  if (img.dataset.fellBack) return;
  img.dataset.fellBack = '1';
  img.src = fallbackPhoto(category, width);
};
